import * as React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BugReportIcon from "@mui/icons-material/BugReport";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { useApi } from "../hooks/useApi";

export default function SquarespacePayloadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const isNew = id === "new";

  const [loading, setLoading] = React.useState(!isNew);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [triggering, setTriggering] = React.useState(false);

  const [source, setSource] = React.useState("squarespace");
  const [status, setStatus] = React.useState("New");
  const [payloadJson, setPayloadJson] = React.useState("{}");
  const [notes, setNotes] = React.useState("");
  const [metaData, setMetaData] = React.useState(null);
  const [createdAt, setCreatedAt] = React.useState(null);
  const [updatedAt, setUpdatedAt] = React.useState(null);
  const [debugModalOpen, setDebugModalOpen] = React.useState(false);
  const [triggerConfirmOpen, setTriggerConfirmOpen] = React.useState(false);

  const fetchPayload = React.useCallback(async () => {
    if (!id || isNew) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/admin/squarespace/payloads/${id}`);
      setSource(data.source || "squarespace");
      setStatus(data.status || "New");
      setPayloadJson(
        typeof data.payload === "string"
          ? data.payload
          : JSON.stringify(data.payload || {}, null, 2)
      );
      setNotes(data.notes || "");
      setMetaData(data.meta_data ?? null);
      setCreatedAt(data.created_at);
      setUpdatedAt(data.updated_at);
    } catch (err) {
      setError(err.message || "Failed to fetch payload");
    } finally {
      setLoading(false);
    }
  }, [id, isNew, api]);

  React.useEffect(() => {
    fetchPayload();
  }, [fetchPayload]);

  const handleSave = async () => {
    let parsedPayload = {};
    try {
      parsedPayload = JSON.parse(payloadJson || "{}");
    } catch {
      setError("Invalid JSON in payload");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const body = {
        source: source || "squarespace",
        payload: parsedPayload,
        notes: notes || null,
      };

      if (isNew) {
        const created = await api.post("/admin/squarespace/payloads", body);
        navigate(`/dashboard/squarespace/payloads/${created.id}`);
      } else {
        const updated = await api.patch(`/admin/squarespace/payloads/${id}`, body);
        setPayloadJson(
          typeof updated.payload === "string"
            ? updated.payload
            : JSON.stringify(updated.payload || {}, null, 2)
        );
        setUpdatedAt(updated.updated_at);
      }
    } catch (err) {
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerConfirm = async () => {
    setTriggering(true);
    setError(null);
    try {
      await api.patch(`/admin/squarespace/payloads/${id}`, { status: "new" });
      await api.post(`/admin/squarespace/payloads/${id}/trigger`, {});
      setTriggerConfirmOpen(false);
      await fetchPayload();
    } catch (err) {
      setError(err.message || "Failed to trigger process");
    } finally {
      setTriggering(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this payload?")) return;
    setDeleting(true);
    setError(null);
    try {
      await api.delete(`/admin/squarespace/payloads/${id}`);
      navigate("/dashboard/squarespace/payloads");
    } catch (err) {
      setError(err.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const getDebugJson = React.useCallback(() => {
    let payloadObj = {};
    try {
      payloadObj = JSON.parse(payloadJson || "{}");
    } catch {
      payloadObj = { _parseError: "Invalid JSON in payload field" };
    }
    const debug = {
      ...(id && !isNew && { id }),
      source,
      status,
      payload: payloadObj,
      meta_data: metaData ?? null,
      notes: notes || null,
      ...(createdAt && { created_at: createdAt }),
      ...(updatedAt && { updated_at: updatedAt }),
    };
    return JSON.stringify(debug, null, 2);
  }, [id, isNew, source, status, payloadJson, metaData, notes, createdAt, updatedAt]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleString("en-US");
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box component="main" sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
            <CircularProgress />
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography color="primary" component={Link} to="/dashboard" sx={{ textDecoration: "none", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
                  Home
                </Typography>
                <Typography color="primary" component={Link} to="/dashboard/squarespace" sx={{ textDecoration: "none", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
                  Squarespace
                </Typography>
                <Typography color="primary" component={Link} to="/dashboard/squarespace/payloads" sx={{ textDecoration: "none", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}>
                  Payloads
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {isNew ? "New" : id?.slice(0, 8) + "..."}
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title={isNew ? "New Payload" : "Edit Payload"}
              description={isNew ? "Create a new inbound payload" : "View and edit payload"}
              icon={<StorageIcon sx={{ color: "primary.main" }} />}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<BugReportIcon />}
                onClick={() => setDebugModalOpen(true)}
              >
                JSON Debug
              </Button>
            </SquarespacePageHeader>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 3, flexWrap: { xs: "wrap", lg: "nowrap" } }}>
              <Card sx={{ flex: 1, minWidth: 0 }}>
                <CardContent>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      size="small"
                      label="Source"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      placeholder="e.g. squarespace"
                      sx={{ minWidth: 180 }}
                    />
                    <TextField
                      fullWidth
                      label="Payload (JSON)"
                      value={payloadJson}
                      onChange={(e) => setPayloadJson(e.target.value)}
                      multiline
                      rows={12}
                      sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                      placeholder='{"key": "value"}'
                    />
                    {(() => {
                      const fromPayload = (() => {
                        try {
                          const p = JSON.parse(payloadJson || "{}");
                          return p.meta_data ?? p.metadata ?? (p.customer?.metadata ?? p.customer?.meta_data) ?? null;
                        } catch {
                          return null;
                        }
                      })();
                      const displayMeta = metaData ?? fromPayload;
                      const displayValue = displayMeta != null
                        ? (typeof displayMeta === "string" ? displayMeta : JSON.stringify(displayMeta, null, 2))
                        : "—";
                      return (
                        <TextField
                          fullWidth
                          label="Meta Data (read-only)"
                          value={displayValue}
                          multiline
                          rows={4}
                          InputProps={{ readOnly: true }}
                          sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                        />
                      );
                    })()}
                    <TextField
                      fullWidth
                      label="Notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ width: { xs: "100%", lg: 260 }, flexShrink: 0 }}>
                <CardContent>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {!isNew && (
                      <>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Status</Typography>
                          <Typography variant="body2">{status || "—"}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Created: {formatDate(createdAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Updated: {formatDate(updatedAt)}
                        </Typography>
                      </>
                    )}
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : isNew ? "Create" : "Save"}
                    </Button>
                    {!isNew && (
                      <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        startIcon={triggering ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                        onClick={() => setTriggerConfirmOpen(true)}
                        disabled={triggering}
                      >
                        {triggering ? "Triggering..." : "Trigger Event"}
                      </Button>
                    )}
                    {!isNew && (
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                        onClick={handleDelete}
                        disabled={deleting}
                      >
                        {deleting ? "Deleting..." : "Delete"}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Container>
          <Footer />
        </Box>
      </Box>

      <Dialog
        open={triggerConfirmOpen}
        onClose={() => !triggering && setTriggerConfirmOpen(false)}
      >
        <DialogTitle>Process Payload</DialogTitle>
        <DialogContent>
          <Typography>Do you want to process the payload?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will set the status to New and trigger the Inngest process job.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerConfirmOpen(false)} disabled={triggering}>
            No
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleTriggerConfirm}
            disabled={triggering}
            startIcon={triggering ? <CircularProgress size={16} /> : null}
          >
            {triggering ? "Triggering..." : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={debugModalOpen}
        onClose={() => setDebugModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: "60vh" } }}
      >
        <DialogTitle>JSON Debug</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            value={getDebugJson()}
            InputProps={{ readOnly: true }}
            sx={{
              fontFamily: "monospace",
              fontSize: "0.8rem",
              mt: 1,
              "& .MuiInputBase-input": { whiteSpace: "pre-wrap", wordBreak: "break-all" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigator.clipboard.writeText(getDebugJson())}>
            Copy to clipboard
          </Button>
          <Button onClick={() => setDebugModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
